import { FragnoClientError, FragnoClientFetchAbortError, FragnoClientFetchError, FragnoClientUnknownApiError } from "../client-error.js";

//#region src/client/internal/ndjson-streaming.ts
/**
* Creates a promise that rejects when the abort signal is triggered
*/
function createAbortPromise(abortSignal) {
	return new Promise((_, reject) => {
		const abortHandler = () => {
			reject(new FragnoClientFetchAbortError("Operation was aborted"));
		};
		if (abortSignal.aborted) abortHandler();
		else abortSignal.addEventListener("abort", abortHandler, { once: true });
	});
}
/**
* Handles NDJSON streaming responses by returning the first item from the fetcher
* and then continuing to stream updates via the store's mutate method.
*
* This makes it so that we can wait until the first chunk before updating the store, if we did
* not do this, `loading` would briefly be false before the first item would be populated in the
* result.
*
* @param response - The fetch Response object containing the NDJSON stream
* @param store - The fetcher store to update with streaming data
* @param abortSignal - Optional AbortSignal to cancel the streaming operation
* @returns A promise that resolves to an object containing the first item and a streaming promise
*/
async function handleNdjsonStreamingFirstItem(response, store, options = {}) {
	if (!response.body) throw new FragnoClientFetchError("Streaming response has no body", "NO_BODY");
	const { abortSignal } = options;
	if (abortSignal?.aborted) throw new FragnoClientFetchAbortError("Operation was aborted");
	const decoder = new TextDecoder();
	const reader = response.body.getReader();
	let buffer = "";
	let firstItem = null;
	const items = [];
	try {
		while (firstItem === null) {
			if (abortSignal?.aborted) {
				reader.releaseLock();
				throw new FragnoClientFetchAbortError("Operation was aborted");
			}
			const { done, value } = await (abortSignal ? Promise.race([reader.read(), createAbortPromise(abortSignal)]) : reader.read());
			if (done) break;
			buffer += decoder.decode(value, { stream: true });
			const lines = buffer.split("\n");
			buffer = lines.pop() || "";
			for (const line of lines) {
				if (!line.trim()) continue;
				try {
					const jsonObject = JSON.parse(line);
					items.push(jsonObject);
					if (firstItem === null) {
						firstItem = jsonObject;
						const streamingPromise = continueStreaming(reader, decoder, buffer, items, store, abortSignal);
						return {
							firstItem,
							streamingPromise
						};
					}
				} catch (parseError) {
					throw new FragnoClientUnknownApiError("Failed to parse NDJSON line", 500, { cause: parseError });
				}
			}
		}
		if (firstItem === null) {
			reader.releaseLock();
			throw new FragnoClientUnknownApiError("NDJSON stream contained no valid items", 500);
		}
		reader.releaseLock();
		throw new FragnoClientFetchError("Unexpected end of stream processing", "NO_BODY");
	} catch (error) {
		if (error instanceof FragnoClientError) {
			store?.setError(error);
			throw error;
		} else {
			const clientError = new FragnoClientUnknownApiError("Unknown streaming error", 500, { cause: error });
			store?.setError(clientError);
			throw clientError;
		}
	}
}
/**
* Continues streaming the remaining items in the background
*/
async function continueStreaming(reader, decoder, initialBuffer, items, store, abortSignal) {
	let buffer = initialBuffer;
	try {
		while (true) {
			if (abortSignal?.aborted) throw new FragnoClientFetchAbortError("Operation was aborted");
			const { done, value } = await (abortSignal ? Promise.race([reader.read(), createAbortPromise(abortSignal)]) : reader.read());
			if (done) {
				if (buffer.trim()) {
					const lines$1 = buffer.split("\n");
					for (const line of lines$1) {
						if (!line.trim()) continue;
						try {
							const jsonObject = JSON.parse(line);
							items.push(jsonObject);
							store?.setData([...items]);
						} catch (parseError) {
							throw new FragnoClientUnknownApiError("Failed to parse NDJSON line", 400, { cause: parseError });
						}
					}
				}
				break;
			}
			buffer += decoder.decode(value, { stream: true });
			const lines = buffer.split("\n");
			buffer = lines.pop() || "";
			for (const line of lines) {
				if (!line.trim()) continue;
				try {
					const jsonObject = JSON.parse(line);
					items.push(jsonObject);
					store?.setData([...items]);
				} catch (parseError) {
					throw new FragnoClientUnknownApiError("Failed to parse NDJSON line", 400, { cause: parseError });
				}
			}
		}
	} catch (error) {
		if (error instanceof FragnoClientError) store?.setError(error);
		else {
			const clientError = new FragnoClientUnknownApiError("Unknown streaming error", 400, { cause: error });
			store?.setError(clientError);
			throw clientError;
		}
		throw error;
	} finally {
		reader.releaseLock();
	}
	return items;
}

//#endregion
export { handleNdjsonStreamingFirstItem };
//# sourceMappingURL=ndjson-streaming.js.map